import { Articles } from "../../data/models/article";
import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import Routes from "router/routes";

gsap.registerPlugin(ScrollTrigger);

interface ArticlesComponentProps {
  article: Articles;
}

const Circle = () => {
  return <div className="h-1 w-1 rounded-full bg-[#B3B3B3]" />;
};

export const ArticlesComponent = ({ article }: ArticlesComponentProps) => {
  const articleRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLParagraphElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const metaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Image animation with scale and rotation
      gsap.fromTo(
        imageRef.current,
        {
          opacity: 0,
          y: 100,
          scale: 0.8,
          rotateX: -15,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          rotateX: 0,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: articleRef.current,
            start: "top bottom-=100",
            end: "top center",
            toggleActions: "play none none reverse",
            scrub: 0.5,
          },
        },
      );

      // Meta info animation
      gsap.fromTo(
        metaRef.current?.children || [],
        {
          opacity: 0,
          x: -20,
          scale: 0.9,
        },
        {
          opacity: 1,
          x: 0,
          scale: 1,
          duration: 0.8,
          stagger: 0.15,
          ease: "back.out(1.2)",
          scrollTrigger: {
            trigger: metaRef.current,
            start: "top bottom-=50",
            toggleActions: "play none none reverse",
          },
        },
      );

      // Title animation
      gsap.fromTo(
        titleRef.current,
        {
          opacity: 0,
          y: 30,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: titleRef.current,
            start: "top bottom-=30",
            toggleActions: "play none none reverse",
          },
        },
      );

      // Description animation
      gsap.fromTo(
        descriptionRef.current,
        {
          opacity: 0,
          y: 20,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power1.out",
          scrollTrigger: {
            trigger: descriptionRef.current,
            start: "top bottom-=20",
            toggleActions: "play none none reverse",
          },
        },
      );
    });

    return () => ctx.revert();
  }, []);

  const navigate = useNavigate();

  if (!article) {
    return (
      <div className="flex h-[600px] animate-pulse flex-col gap-4 rounded-[20px] bg-gray-700"></div>
    );
  }

  const formattedDate = format(new Date(article.createdAt ?? ""), "yyyy");

  return (
    <div
      onClick={() => {
        navigate(Routes.BLOG_POST(article.slug), {
          state: article,
        });
      }}
      ref={articleRef}
      className="flex cursor-pointer flex-col gap-4"
    >
      <div
        ref={imageRef}
        className="h-[500px] w-full overflow-hidden rounded-[20px]"
      >
        <img
          src={`${process.env.REACT_APP_STRAPI_URL}${article.cover?.formats.large.url}`}
          alt={article.title}
          className="h-full w-full object-cover"
        />
      </div>
      <div ref={contentRef} className="flex flex-col gap-2">
        <h3
          ref={titleRef}
          className="font-inter text-[20px] font-medium leading-[140%] tracking-[-0.02em] text-[#FFFEFD]"
        >
          {article.title}
        </h3>
        <p
          ref={descriptionRef}
          className="font-inter text-[16px] leading-[140%] text-[#5A5A59]"
        >
          {article.description}...
        </p>
      </div>
      <div ref={metaRef} className="flex items-center gap-2">
        <p className="font-inter text-[16px] font-medium leading-[140%] text-[#B3B3B3] sm:text-[20px]">
          {formattedDate}
        </p>
        <Circle />
        <p className="font-inter text-[16px] font-medium leading-[140%] text-[#B3B3B3] sm:text-[20px]">
          {article.author}
        </p>
      </div>
    </div>
  );
};
